import { Box, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import type { ReactNode } from 'react';
import { geStageFromDate } from '/@/components/crop-monitoring/progress-bar/assets/BASE_STEPS';
import { _ } from '/@/locale';
import type { CropMonitoring } from '/@/types/crop-monitoring';

export interface TimelineEntry {
    id: string | number;
    /** ISO date that places the entry on the campaign timeline. */
    date: string;
    /** Registry card rendered to the right of the rail. */
    children: ReactNode;
    /**
     * Forces this entry's rail state instead of deriving one from `cropMonitoring` — used for
     * pseudo-entries with no real crop data yet, e.g. the "next crop" prompt shown during
     * fallow once herbicide recommendations/applications exist.
     */
    state?: CropState;
}

export interface CropState {
    /** Short bubble code, e.g. "S", "V4", "R1", "C", or "B" (Barbecho). */
    code: string;
    /** Date the state started. Absent for Barbecho (everything before S has no date). */
    date?: string;
}

const RAIL_WIDTH = 96;
const BUBBLE_SIZE = 32;
/** Centers the 2px connector line under the right-aligned 32px bubble (32/2 - 1). */
const CONNECTOR_PADDING = '15px';

/**
 * Crop-monitoring state at a given date, with the date that state started.
 * "B" (Barbecho) covers everything before planting and carries no date.
 * Returns undefined when there is no corn monitoring data to derive a state from.
 */
export const getCropState = (date: string, cropMonitoring?: CropMonitoring): CropState | undefined => {
    // No active crop monitoring at all → the whole field is in fallow ("Barbecho"), not just
    // the entries that predate a planting date.
    if (!cropMonitoring) return { code: 'B' };

    const corn = cropMonitoring.cornCropMonitoring;
    if (!corn?.stages?.length) return undefined;

    const entryDate = dayjs(date);
    const plantingDate = dayjs(cropMonitoring.plantingDate);
    if (entryDate.isBefore(plantingDate, 'day')) return { code: 'B' };

    const result = geStageFromDate(entryDate, corn.stages, plantingDate) as
        | { stage?: { date?: string }; step?: { displayName?: string } }
        | undefined;
    const code = result?.step?.displayName;
    if (!code) return undefined;

    return { code, date: result?.stage?.date ?? cropMonitoring.plantingDate };
};

const Timeline = ({ entries, cropMonitoring }: { entries: TimelineEntry[]; cropMonitoring?: CropMonitoring }) => {
    // Newest first.
    const ordered = [...entries].reverse();

    // No active crop monitoring → there's no real stage progression to visualize, so skip the
    // rail entirely (no bubbles, state labels, or connecting line) and just stack the cards.
    if (!cropMonitoring) {
        return (
            <Stack spacing={4}>
                {ordered.map((entry) => (
                    <Box key={entry.id}>{entry.children}</Box>
                ))}
            </Stack>
        );
    }

    // Group consecutive entries that share a crop-monitoring state: the first row of a group
    // shows the filled bubble with the state code + the state's date, the rest show a hollow
    // connector node. Falls back to grouping by day when there is no crop-monitoring state.
    const rows = ordered.map((entry) => {
        const state = entry.state ?? getCropState(entry.date, cropMonitoring);
        return {
            entry,
            code: state?.code,
            date: state ? state.date : entry.date,
            groupKey: state?.code ?? `day:${dayjs(entry.date).format('YYYY-MM-DD')}`,
        };
    });

    return (
        <Stack>
            {rows.map((row, index) => {
                const isHeader = index === 0 || rows[index - 1]?.groupKey !== row.groupKey;
                const isLast = index === rows.length - 1;
                const dateLabel = isHeader ? row.date : undefined;

                return (
                    <Stack key={row.entry.id} direction='row' spacing={2} alignItems='stretch'>
                        <Stack alignItems='flex-end' sx={{ width: RAIL_WIDTH, flexShrink: 0 }}>
                            <Stack
                                direction='row'
                                spacing={0.5}
                                alignItems='center'
                                justifyContent='flex-end'
                                sx={{ width: RAIL_WIDTH }}
                            >
                                {dateLabel && (
                                    <Typography
                                        variant='caption'
                                        fontWeight='bold'
                                        color='primary'
                                        sx={{ whiteSpace: 'nowrap' }}
                                    >
                                        {dayjs(dateLabel).format('DD/MM/YY')}
                                    </Typography>
                                )}
                                {row.code === 'B' && isHeader && (
                                    <Typography
                                        variant='caption'
                                        fontWeight='bold'
                                        color='primary'
                                        sx={{ whiteSpace: 'nowrap' }}
                                    >
                                        {_('Barbecho')}
                                    </Typography>
                                )}
                                {row.code === 'S' && isHeader && !dateLabel && (
                                    <Typography
                                        variant='caption'
                                        fontWeight='bold'
                                        color='primary'
                                        sx={{ whiteSpace: 'nowrap' }}
                                    >
                                        {_('Siembra')}
                                    </Typography>
                                )}
                                {row.code === 'C' && isHeader && !dateLabel && (
                                    <Typography
                                        variant='caption'
                                        fontWeight='bold'
                                        color='primary'
                                        sx={{ whiteSpace: 'nowrap' }}
                                    >
                                        {_('Cosecha')}
                                    </Typography>
                                )}
                                <Box
                                    sx={{
                                        width: BUBBLE_SIZE,
                                        height: BUBBLE_SIZE,
                                        borderRadius: '50%',
                                        flexShrink: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        // State headers are filled (most recent = highlighted, rest = tinted);
                                        // same-state repeats fall through to the hollow donut below.
                                        ...(isHeader && {
                                            bgcolor: index === 0 ? 'primary.onSurface' : 'primary.infoBackground',
                                        }),
                                    }}
                                >
                                    {isHeader ? (
                                        row.code && (
                                            <Typography
                                                variant='subtitle2'
                                                fontWeight='bold'
                                                sx={{ color: index === 0 ? 'common.white' : 'primary.main' }}
                                            >
                                                {row.code}
                                            </Typography>
                                        )
                                    ) : (
                                        // Hollow "donut" node: faint outer ring + white gap + faint inner dot.
                                        <Box
                                            sx={{
                                                width: 28,
                                                height: 28,
                                                borderRadius: '50%',
                                                boxSizing: 'border-box',
                                                border: 4,
                                                borderColor: 'primary.mui.stroke',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 16,
                                                    height: 16,
                                                    borderRadius: '50%',
                                                    bgcolor: 'primary.mui.stroke',
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </Stack>
                            {!isLast && (
                                <Stack flex={1} alignItems='center' sx={{ pr: CONNECTOR_PADDING }}>
                                    <Box sx={{ width: 2, flex: 1, bgcolor: 'primary.mui.stroke' }} />
                                </Stack>
                            )}
                        </Stack>
                        <Box sx={{ flex: 1, minWidth: 0, pb: 4 }}>{row.entry.children}</Box>
                    </Stack>
                );
            })}
        </Stack>
    );
};

export default Timeline;
